import { useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@redux/RootReducer';
import { useForm, useStore } from '@tanstack/react-form';

import { Button, Icon, InputField, Modal, SelectField, Spinner, Text } from '@nvision/ui';

import { useValidationSchema, VALIDATION_SCHEMA_KEYS } from '@/modules/validators';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { mapAddAddressPayload, mapUpdateAddressPayload } from '../../../data-provider/mappers';
import { useAddAddress, useAddressById, useCountries, useStates, useUpdateAddress } from '../../../hooks';
import { type AddressFormValues, addressFormSchema, type Country, type State } from '../../../models';
import { createAddressFormValidator, mapApiErrorsToFormFields } from './address-form-validator';

import styles from './address-modal.module.css';

interface AddressModalProps {
  mode: 'add' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  addressId?: string;
  onCreated?: (addressId: string) => void;
}

const defaultAddressFormValues = (): AddressFormValues =>
  ({
    address1: '',
    address2: '',
    city: '',
    country: { id: '', label: '' },
    name: '',
    postalCode: '',
    state: { id: '', label: '' },
  }) as AddressFormValues;

type FieldError = {
  message?: string;
};

const getErrorMessage = (errors?: FieldError[]) => errors?.map((e) => e.message ?? '').join('. ');

export const AddressModal: React.FC<AddressModalProps> = ({ mode, isOpen, onClose, addressId, onCreated }) => {
  const isEdit = mode === 'edit';

  const { data: address, isLoading: addressLoading } = useAddressById(addressId);
  const { mutateAsync: addAddress, isPending: isAdding } = useAddAddress();
  const { mutateAsync: updateAddress, isPending: isUpdating } = useUpdateAddress();
  const { data: countries, isLoading: countriesLoading } = useCountries();

  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);

  const { data: apiSchema } = useValidationSchema(VALIDATION_SCHEMA_KEYS.addressBook.record);

  const formValidator = useMemo(
    () => (apiSchema ? createAddressFormValidator(apiSchema.validationSchema) : null),
    [apiSchema],
  );

  const handleClose = () => {
    form.reset(defaultAddressFormValues());
    onClose();
  };

  const form = useForm({
    defaultValues: defaultAddressFormValues(),

    onSubmit: async ({ value }) => {
      try {
        if (isEdit && addressId) {
          const payload = mapUpdateAddressPayload(
            addressId,
            value,
            selectedCompany.companyId,
            selectedCompany.companyName,
          );

          await updateAddress(payload);

          showNotification({
            message: 'Address updated successfully',
            type: NOTIFICATION_TYPES.SUCCESS,
          });
        } else {
          const payload = mapAddAddressPayload(value, selectedCompany.companyId, selectedCompany.companyName);

          const createdAddress = await addAddress(payload);
          const createdAddressId = String(createdAddress?.id);

          showNotification({
            message: 'Address added successfully',
            type: NOTIFICATION_TYPES.SUCCESS,
          });

          if (createdAddressId) {
            onCreated?.(createdAddressId);
          }
        }

        handleClose();
      } catch {
        showNotification({
          message: isEdit ? 'Failed to update address' : 'Failed to add address',
          type: NOTIFICATION_TYPES.ERROR,
        });
      }
    },

    validators: {
      // @ts-expect-error
      // TODO: fix types
      onChange: formValidator ?? addressFormSchema,
      // @ts-expect-error
      // TODO: fix types
      onSubmit: formValidator ?? addressFormSchema,
    },
  });

  const country = useStore(form.store, (s) => s.values.country);

  const { data: states, isLoading: statesLoading } = useStates({
    CountryCode: country?.id ?? '',
  });

  const rawErrorMap = useStore(form.store, (s) => s.errorMap.onSubmit);

  const errorMap = useMemo(() => mapApiErrorsToFormFields(rawErrorMap), [rawErrorMap]);

  useEffect(() => {
    if (!isEdit || !address) {
      return;
    }

    form.setFieldValue('name', address.name ?? '');
    form.setFieldValue('city', address.city);
    form.setFieldValue('postalCode', address.postalCode);
    form.setFieldValue('address1', address.address1);
    form.setFieldValue('address2', address.address2);

    form.setFieldValue('country', {
      id: address.countryCode ?? '',
      label: address.country,
    });

    form.setFieldValue('state', {
      id: address.stateCode ?? '',
      label: address.state,
    });
  }, [isEdit, address, form]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    form.validate('submit');
    form.handleSubmit();
  };

  const handleCountryChange = (country: Country | null, onChange: (value: { id: string; label: string }) => void) => {
    if (country) {
      onChange({ id: String(country.id), label: country.label });

      form.setFieldValue('state', {
        id: '',
        label: '',
      });
    }
  };

  const isPending = isAdding || isUpdating;

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && handleClose()} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          {isEdit ? 'Edit Address' : 'Add Address'}
        </Text>
        <Icon name="x" cursor="pointer" onClick={handleClose} size={20} />
      </Modal.Header>

      {apiSchema ? (
        <>
          <Modal.Content className={styles.content}>
            <form className={styles.form} onSubmit={onSubmit}>
              <div className={styles.grid}>
                <form.Field name="name">
                  {(field) => (
                    <InputField
                      className={styles.fieldName}
                      isLoading={addressLoading}
                      label="Company Name"
                      placeholder="Enter"
                      size="sm"
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>

                <form.Field name="country">
                  {(field) => (
                    <SelectField
                      className={styles.fieldCountry}
                      isLoading={countriesLoading || addressLoading}
                      label="Country"
                      options={countries?.list ?? []}
                      placeholder="Select"
                      size="sm"
                      isRequired
                      value={field.state.value as unknown as Country | null}
                      onBlur={field.handleBlur}
                      errorMessage={getErrorMessage(errorMap.country)}
                      onSelectionChange={(c) => handleCountryChange(c, field.handleChange)}
                    />
                  )}
                </form.Field>

                <form.Field name="state">
                  {(field) => (
                    <SelectField
                      className={styles.fieldState}
                      isLoading={statesLoading || addressLoading}
                      label="State/Province"
                      options={states?.list ?? []}
                      placeholder="Select"
                      size="sm"
                      isRequired
                      value={field.state.value as unknown as State | null}
                      onBlur={field.handleBlur}
                      errorMessage={getErrorMessage(errorMap.state)}
                      onSelectionChange={(s) => s && field.handleChange({ id: String(s.id), label: s.label })}
                    />
                  )}
                </form.Field>

                <form.Field name="postalCode">
                  {(field) => (
                    <InputField
                      className={styles.fieldPost}
                      isLoading={addressLoading}
                      isRequired
                      label="Post Code"
                      placeholder="Enter"
                      size="sm"
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      errorMessage={getErrorMessage(errorMap.postalCode)}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>

                <form.Field name="city">
                  {(field) => (
                    <InputField
                      className={styles.fieldCity}
                      isLoading={addressLoading}
                      isRequired
                      label="City"
                      placeholder="Enter"
                      size="sm"
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      errorMessage={getErrorMessage(errorMap.city)}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>

                <form.Field name="address1">
                  {(field) => (
                    <InputField
                      className={styles.fieldAddr1}
                      isLoading={addressLoading}
                      label="Address 1"
                      placeholder="Enter"
                      size="sm"
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      errorMessage={getErrorMessage(errorMap.address1)}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>

                <form.Field name="address2">
                  {(field) => (
                    <InputField
                      className={styles.fieldAddr2}
                      isLoading={addressLoading}
                      label="Address 2"
                      placeholder="Enter"
                      size="sm"
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      errorMessage={getErrorMessage(errorMap.address2)}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>
              </div>
            </form>
          </Modal.Content>

          <Modal.Footer className={styles.footer}>
            {!isEdit && (
              <Button
                className={styles.clearAll}
                ghost
                iconLeft="rotate-ccw"
                onClick={() => form.reset(defaultAddressFormValues())}
              >
                Clear all details
              </Button>
            )}

            <div className={styles.footerActions}>
              <Button outline secondary onPress={handleClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isPending} onClick={onSubmit}>
                {isEdit ? 'Save' : 'Add'}
              </Button>
            </div>
          </Modal.Footer>
        </>
      ) : (
        <Modal.Content className={styles.loadingContainer}>
          <Spinner />
        </Modal.Content>
      )}
    </Modal>
  );
};
